import { DatePipe } from '@angular/common';
import { Component, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { StringUtils } from '@pk/powerkioskutils';
import { RouterService } from 'src/app/core/router.service';

import _ from 'lodash';
import moment from 'moment-timezone';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

import { AuctionBidEditComponent } from 'src/app/auction/auction-bid-edit/auction-bid-edit.component';
import { HelperService } from 'src/app/shared/helper.service';
import { Holiday, RfqSession, RfqSessionProduct } from 'src/app/shared/models';
import { AbstractPageForm } from 'src/app/shared/pages/page-form.abstract';
import { AuctionExtendComponent } from '../../../auction/auction-extend/auction-extend.component';
import { AuctionInProgressComponent } from '../../../auction/auction-in-progress/auction-in-progress.component';
import { AuctionSupplierCreateBidsComponent } from '../../../auction/auction-supplier-create-bids/auction-supplier-create-bids.component';
import { AuctionSupplierNoteEditComponent } from '../../../auction/auction-supplier-note-edit/auction-supplier-note-edit.component';
import { GraphqlService } from '../../../graphql/graphql.service';
import { Product, RfqSessionBid, Supplier } from '../../../shared/models';

@Component({
	selector: 'pk-broker-contract-auction-in-progress',
	templateUrl: './contract-auction-in-progress.component.html',
	styleUrls: ['./contract-auction-in-progress.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ContractAuctionInProgressModalComponent extends AbstractPageForm implements OnDestroy {
	@ViewChild(AuctionInProgressComponent, { static: false }) auction: AuctionInProgressComponent;
	@ViewChild(AuctionSupplierCreateBidsComponent, { static: false }) auctionSupplierCreateBids: AuctionSupplierCreateBidsComponent;
	@ViewChild(AuctionExtendComponent, { static: false }) auctionExtend: AuctionExtendComponent;
	@ViewChild(AuctionSupplierNoteEditComponent, { static: false }) auctionSupplierNoteEdit: AuctionSupplierNoteEditComponent;
	@ViewChild(AuctionBidEditComponent, { static: false }) auctionBidEdit: AuctionBidEditComponent;

	public onSuccess = new Subject<RfqSession>();
	public onExtend = new Subject<RfqSession>();

	public rfqSessionId: string;
	public contractId: string;
	public serviceTypeId: string;
	public stateId: string;
	public rfqSession: RfqSession;
	public rfqSessionProducts: RfqSessionProduct[] = [];
	public products: Product[];
	public suppliers: Supplier[];
	public otherSuppliers: Supplier[];
	public holidays: Holiday[];
	public defaultProducts: Product[] = [];

	public bidsSupplierId: string;
	public bid: RfqSessionBid;
	public auctionView: 'auction' | 'help' | 'supplierCreateBids' | 'supplierBids' | 'supplierNoteEdit' | 'extend' | 'bidEdit' = 'auction';

	public noteKey: string;
	public noteKeyDisplay: string;

	private interval: number;
	private route: ActivatedRoute;

	constructor(
		public activeModal: BsModalRef,
		private datePipe: DatePipe,
		private routerService: RouterService,
		private graphqlService: GraphqlService,
		private helperService: HelperService,
	) {
		super();
	}

	public init(contractId: string, serviceTypeId: string, stateId: string, rfqSessionId: string, route?: ActivatedRoute): void {
		this.rfqSessionId = rfqSessionId;
		this.serviceTypeId = serviceTypeId;
		this.stateId = stateId;
		this.contractId = contractId;
		this.route = route;

		this.getAuctionInProgressData(true, false);

		this.interval = window.setInterval(() => {
			if (this.auctionView === 'auction') {
				this.refreshBids();
			}
		}, 30000);
	}

	get agentIsOnHold(): boolean {
		return this.loggedInUser.agent?.holdEnrollments;
	}

	private refreshBids(): void {
		this.getRefreshAuctionInProgressData();
	}

	private async getRefreshAuctionInProgressData(): Promise<void> {
		try {
			this.warnings = [];
			const contractResult = await this.graphqlService.getRefreshAuctionInProgressModalData(this.rfqSessionId);
			contractResult.data.rfqSession.contract = this.rfqSession.contract;
			const refreshedRfqSession = new RfqSession(contractResult.data.rfqSession);
			this.rfqSession.suppliers = refreshedRfqSession.suppliers;
			this.rfqSession.bids = refreshedRfqSession.bids;
			this.rfqSession.bestBids = refreshedRfqSession.bestBids;
			this.rfqSession.products = refreshedRfqSession.products;

			await this.setupProductsAndSuppliers();
		} catch (e) {
			this.warnings = [`There was a problem refreshing the auction. Trying again...`];
		} finally {
			this.refreshView();
		}
	}

	private async getAuctionInProgressData(loadingInitialVal: boolean, loadingFinalVal: boolean): Promise<void> {
		this.loading = loadingInitialVal;
		this.warnings = [];
		try {
			if (this.contractId && !this.rfqSessionId) {
				const rfqSessionResult = await this.graphqlService.getRfqSessionContract(this.contractId);
				this.rfqSessionId = rfqSessionResult.data.contract.rfqSessionId;
			}

			const dateGTE = this.datePipe.transform(new Date(), 'yyyy-MM-ddT00:00:00');

			const auctionResult = await this.graphqlService.getAuctionInProgressModalData(this.rfqSessionId, this.serviceTypeId, this.stateId, dateGTE);
			this.rfqSession = new RfqSession(auctionResult.data.rfqSession);
			this.products = auctionResult.data.products.message.map(p => new Product(p));
			this.holidays = auctionResult.data.holidays.message.map(h => new Holiday(h));
			if (this.rfqSession.contract.productId) {
				this.defaultProducts.push({
					productId: this.rfqSession.contract.productId,
				} as Product);
			} else {
				this.defaultProducts.push(this.getDefaultProduct());
			}

			await this.setupProductsAndSuppliers();

			if (this.route) {
				const showExtend = StringUtils.toBoolean(this.route.snapshot.queryParamMap.get('showExtend'));
				if (showExtend) {
					this.onShowAuctionExtendClicked();
				}
			}
		} catch (e) {
			this.warnings = [
				`There was a problem loading the pricing session. We have been ` +
					`notified and are working to fix the issue. Please check back again in 30 minutes.`,
			];
		} finally {
			this.loading = loadingFinalVal;
		}
	}

	private async setupProductsAndSuppliers(): Promise<void> {
		const sortedProducts = _.orderBy(
			this.rfqSession.products,
			[obj => obj.product.sortOrder, obj => obj.term, obj => obj.greenPercentage],
			['asc', 'asc', 'asc'],
		);
		for (let i = 0; i < sortedProducts.length; i++) {
			// keep the row open if it was already open
			let shown = false;
			if (this.rfqSessionProducts[i] && this.rfqSessionProducts[i]['shown']) {
				shown = true;
			}
			// assign to the product individually, keep overall array ref unchanged
			this.rfqSessionProducts[i] = sortedProducts[i];
			this.rfqSessionProducts[i]['shown'] = shown;
		}

		this.rfqSessionProducts = _.orderBy(
			this.rfqSessionProducts,
			[obj => obj.product.sortOrder, obj => obj.term, obj => obj.greenPercentage],
			['asc', 'asc', 'asc'],
		);
		this.rfqSession.products = this.rfqSessionProducts;
		this.rfqSession.suppliers = _.orderBy(this.rfqSession.suppliers, obj => obj.supplier.name);
		const availableSupplierResult = await this.graphqlService.getAvailableSuppliers(
			this.serviceTypeId,
			this.stateId,
			this.rfqSession.contract.getUtilityId(),
			this.rfqSession.contract.annualUsage,
			null,
			moment(this.rfqSession.contract.effectiveDate).format('YYYY-MM-DD'),
		);
		this.suppliers = _.orderBy(
			availableSupplierResult.data.availableSuppliers.message.map(s => new Supplier(s)),
			s => s.name,
		);
		const supplierIds = this.suppliers.map(s => s.supplierId);
		const otherSuppliers = availableSupplierResult.data.otherSuppliers.message
			.map(s => new Supplier(s))
			.filter(s => !supplierIds.includes(s.supplierId));
		this.otherSuppliers = _.orderBy(otherSuppliers, s => s.name);
		this.rfqSession.suppliers.forEach(s => {
			s.setDocumentRequestNotes(this.rfqSession.contract.attachments);
		});
	}

	private getDefaultProduct(): Product {
		let defaultProduct = this.products.find(p =>
			[this.CONSTANTS.products.fixedAllInElectric, this.CONSTANTS.products.fixedAllInGas].includes(p.productId),
		);
		if (!defaultProduct) {
			defaultProduct = this.products[0];
		}

		return defaultProduct;
	}

	public onBidsSubmitted(): void {
		this.refreshBids();
	}

	public async updateRfqSession(): Promise<void> {
		await this.auction.submit();
	}

	public submitBids(): void {
		this.auctionSupplierCreateBids.submit();
	}

	public submitBidEdit(): void {
		this.auctionBidEdit.submit();
	}

	public extendSession(): void {
		this.auctionExtend.submit();
	}

	public submitSupplierNote(): void {
		this.auctionSupplierNoteEdit.submit();
	}

	public onAuctionCreateCompleted(rfqSession: RfqSession): void {
		window.clearInterval(this.interval);
		this.init(this.contractId, this.serviceTypeId, this.stateId, this.rfqSessionId, this.route);
		this.onSuccess.next(rfqSession);
	}

	public onAuctionUpdateCompleted(rfqSession: RfqSession): void {
		window.clearInterval(this.interval);
		this.init(this.contractId, this.serviceTypeId, this.stateId, this.rfqSessionId, this.route);
		this.onSuccess.next(rfqSession);
	}

	public onAuctionExtendCompleted(rfqSession: RfqSession): void {
		this.onExtend.next(rfqSession);
	}

	public onShowAuctionSupplierBidsClicked(supplierId: string): void {
		this.bidsSupplierId = supplierId;
		this.auctionView = 'supplierBids';
	}

	public onShowAuctionBidEditClicked(bid: RfqSessionBid): void {
		this.bid = bid;
		this.auctionView = 'bidEdit';
	}

	public onShowAuctionSupplierCreateBidsClicked(supplierId: string): void {
		this.bidsSupplierId = supplierId;
		this.auctionView = 'supplierCreateBids';
	}

	public onShowAuctionSupplierNoteEditClicked({
		supplierId,
		noteKey,
		noteKeyDisplay,
	}: {
		supplierId: string;
		noteKey: string;
		noteKeyDisplay: string;
	}): void {
		this.bidsSupplierId = supplierId;
		this.auctionView = 'supplierNoteEdit';
		this.noteKey = noteKey;
		this.noteKeyDisplay = noteKeyDisplay;
	}

	public async onShowAuctionExtendClicked(): Promise<void> {
		this.auctionView = 'extend';

		if (this.route) {
			await this.routerService.navigate(['.'], {
				relativeTo: this.route,
				queryParams: {
					showAuction: true,
					showExtend: true,
					auctionContractId: this.contractId,
				},
				queryParamsHandling: 'merge',
			});
		}
	}

	public async backToSession(): Promise<void> {
		this.auctionView = 'auction';

		if (this.route) {
			await this.routerService.navigate(['.'], {
				relativeTo: this.route,
				queryParams: {
					showAuction: true,
					auctionContractId: this.contractId,
				},
				queryParamsHandling: 'merge',
			});
		}
	}

	public onDeletedBid(): void {
		if (!this.loading) {
			this.refreshBids();
		}
	}

	public onBidUpdated(): void {
		this.refreshBids();
		this.backToSession();
	}

	public cancel(): void {
		if (!this.auction || !this.auction.submitDisabled) {
			this.activeModal.hide();
		}
	}

	// eslint-disable-next-line max-len
	public onUpdateAuctionView(auctionView: 'auction' | 'help' | 'supplierCreateBids' | 'supplierBids' | 'supplierNoteEdit' | 'extend'): void {
		this.auctionView = auctionView;
	}

	ngOnDestroy() {
		window.clearInterval(this.interval);
	}
}
